import React from "react";
import {
  FaCheck,
  FaExternalLinkAlt,
  FaEye,
  FaHandshake,
  FaImage,
  FaPen,
  FaTimes,
  FaTrashAlt,
  FaUserSlash,
} from "react-icons/fa";

import {
  getDisplayUrl,
  getMediaUrl,
  getStatusClass,
  getStatusLabel,
} from "../utils/partenairesUtils";

const stopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onMouseDown={stopEvent}
      onClick={(event) => {
        stopEvent(event);
        onClick?.();
      }}
      className={`relative z-20 grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function PartenaireSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-48 animate-pulse bg-slate-100 dark:bg-slate-800" />
      <div className="p-5">
        <div className="h-5 w-32 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function PartenairesGrid({
  loading,
  partenaires = [],
  deleteCandidateId,
  onDetails,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}) {
  if (loading) {
    return (
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <PartenaireSkeleton key={index} />
        ))}
      </section>
    );
  }

  if (!partenaires.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucun partenaire trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou créez un nouveau partenaire.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {partenaires.map((partenaire) => {
        const imageUrl = getMediaUrl(partenaire.photo_url);
        const linkUrl = getDisplayUrl(partenaire.link_url);
        const isDeleting = deleteCandidateId === partenaire.id;

        const openPartnerLink = (event) => {
          stopEvent(event);

          if (!linkUrl) return;

          window.open(linkUrl, "_blank", "noopener,noreferrer");
        };

        return (
          <article
            key={partenaire.id}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            className={`relative overflow-hidden rounded-3xl border bg-white shadow-sm shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80 dark:bg-slate-900 dark:shadow-none ${
              partenaire.is_active
                ? "border-slate-200 dark:border-slate-800"
                : "border-slate-200 opacity-75 dark:border-slate-800"
            }`}
          >
            <div className="relative flex h-56 items-center justify-center bg-slate-50 p-8 dark:bg-slate-800/60">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={partenaire.nom}
                  className="max-h-full max-w-full object-contain transition duration-500 hover:scale-105"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <FaImage className="text-4xl" />
                  <p className="mt-3 text-sm font-medium">
                    Aucun logo renseigné
                  </p>
                </div>
              )}

              <span
                className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                  partenaire.is_active
                )}`}
              >
                {getStatusLabel(partenaire.is_active)}
              </span>

              <span className="absolute bottom-4 left-4 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                Ordre {partenaire.ordre ?? "—"}
              </span>
            </div>

            <div className="p-5">
              <h3 className="line-clamp-2 text-xl font-bold text-slate-950 dark:text-white">
                {partenaire.nom}
              </h3>

              {partenaire.link_url ? (
                <button
                  type="button"
                  onMouseDown={stopEvent}
                  onClick={openPartnerLink}
                  className="relative z-20 mt-3 flex w-full items-center gap-2 truncate rounded-2xl bg-slate-50 px-3 py-2 text-left text-sm font-medium text-blue-600 transition hover:bg-blue-50 hover:text-blue-700 dark:bg-slate-950 dark:text-blue-300 dark:hover:bg-blue-500/10"
                >
                  <FaExternalLinkAlt className="shrink-0 text-xs" />
                  <span className="truncate">{partenaire.link_url}</span>
                </button>
              ) : (
                <div className="mt-3 flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-sm text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                  <FaHandshake className="shrink-0" />
                  Aucun lien renseigné
                </div>
              )}

              <div
                className="relative z-20 mt-5 flex flex-wrap justify-end gap-2"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                {isDeleting ? (
                  <>
                    <button
                      type="button"
                      onMouseDown={stopEvent}
                      onClick={(event) => {
                        stopEvent(event);
                        onConfirmDelete(partenaire.id);
                      }}
                      className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                    >
                      <FaCheck />
                      Confirmer
                    </button>

                    <button
                      type="button"
                      onMouseDown={stopEvent}
                      onClick={(event) => {
                        stopEvent(event);
                        onCancelDelete();
                      }}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      <FaTimes />
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <IconButton
                      title="Voir le détail"
                      icon={FaEye}
                      tone="blue"
                      onClick={() => onDetails(partenaire)}
                    />

                    <IconButton
                      title="Modifier"
                      icon={FaPen}
                      tone="orange"
                      onClick={() => onEdit(partenaire)}
                    />

                    <IconButton
                      title="Supprimer"
                      icon={FaTrashAlt}
                      tone="rose"
                      onClick={() => onAskDelete(partenaire.id)}
                    />
                  </>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}